import React, { useState, useEffect, useCallback, useMemo } from "react";
import {
  Box,
  Button,
  CssBaseline,
  Tooltip,
  Typography,
  useTheme,
} from "@mui/material";
import useMediaQuery from "@mui/material/useMediaQuery";
import { useTheme as themeStyles } from "@mui/material/styles";

import { set, ref, onValue } from "firebase/database";
import { db } from "../../../../../firebase";

import Topbar from "../../../../../components/topbar/TopBar";
import Sidebar from "../../../../../components/sidebar/Sidebar";
import Header from "../../../../../components/header/Header";
import { DataGrid, esES } from "@mui/x-data-grid";

import { tokens } from "../../../../../theme";
import { convertDate } from "../../../../../helpers/date/convertDate";
import SnackBar from "../../../../../components/snackbar/SnackBar";
import {
  decodeAdminToken,
  getToken,
} from "../../../../../helpers/token/refreshToken";
import { ReadBackupEnterprise } from "../usecases/ReadBackupEnteprise";

const PAGE_SIZE = 100;
const INITIAL_PAGES = [1, 2];
const BOOKING_TYPE_LABELS = {
  1: "AHORA",
  2: "RESERVAS",
  3: "HORAS",
  4: "TOURS",
};

function getSubRoutesList(subRoutes) {
  if (!subRoutes) return [];

  if (Array.isArray(subRoutes)) {
    return subRoutes.filter((subRoute) => subRoute?.name);
  }

  if (typeof subRoutes === "object") {
    return Object.values(subRoutes).filter((subRoute) => subRoute?.name);
  }

  if (typeof subRoutes !== "string") return [];

  const value = subRoutes.trim();
  if (!value || value === "[]") return [];

  try {
    const parsedSubRoutes = JSON.parse(value);
    return getSubRoutesList(parsedSubRoutes);
  } catch (error) {
    console.log("No se pudo leer sub_routes", error);
    return [];
  }
}

function getSubRoutesTooltip(subRoutes) {
  return getSubRoutesList(subRoutes)
    .sort((a, b) => Number(a?.id || 0) - Number(b?.id || 0))
    .map((subRoute, index) => `${index + 1}. ${subRoute.name}`)
    .join("\n");
}

function compareBackupByDate(a, b) {
  const dateB = convertDate(b.date, b.time);
  const dateA = convertDate(a.date, a.time);
  return dateB - dateA;
}

const Backup = () => {
  const themeSidebar = themeStyles();
  const matchesSM = useMediaQuery(themeSidebar.breakpoints.down("sm"));
  const theme = useTheme();
  const colors = tokens(theme.palette.mode);
  const dataToken = getToken("adttkn");
  const { company_id } = decodeAdminToken(dataToken);

  const [listBackup, setListBackup] = useState([]);
  const [dataSelected, setDataSelected] = useState([]);
  const [page, setPage] = useState(0);
  const [loadedPages, setLoadedPages] = useState([]);
  const [hasMoreBackups, setHasMoreBackups] = useState(true);

  const loadBackupPages = useCallback(
    async (pagesToLoad) => {
      const nextPages = pagesToLoad.filter(
        (pageNumber) => !loadedPages.includes(pageNumber),
      );

      if (nextPages.length === 0) return;

      try {
        const responses = await Promise.all(
          nextPages.map((pageNumber) => ReadBackupEnterprise(pageNumber)),
        );

        const successfulBackups = responses.flatMap((response) => {
          if (response.message !== "success") {
            console.log(response.message);
            return [];
          }

          return Object.values(response?.backup || {});
        });

        const lastResponse = responses[responses.length - 1];
        const lastResponseBackups = Object.values(lastResponse?.backup || {});

        setHasMoreBackups(lastResponseBackups.length >= PAGE_SIZE);
        setLoadedPages((currentPages) =>
          Array.from(new Set([...currentPages, ...nextPages])),
        );
        setListBackup((currentBackups) => {
          const backupById = new Map(
            currentBackups.map((backup) => [backup.id, backup]),
          );

          successfulBackups.forEach((backup) => {
            if (
              Number(backup.user_type) === 0 &&
              Number(backup.company_id) === Number(company_id)
            ) {
              backupById.set(backup.id, backup);
            }
          });

          return Array.from(backupById.values()).sort(compareBackupByDate);
        });
      } catch (error) {
        console.log(error);
      }
    },
    [company_id, loadedPages],
  );

  useEffect(() => {
    loadBackupPages(INITIAL_PAGES);
  }, [loadBackupPages]);

  const rows = useMemo(() => {
    const start = page * PAGE_SIZE;
    const end = start + PAGE_SIZE;

    return listBackup.slice(start, end);
  }, [listBackup, page]);

  const rowCount = hasMoreBackups
    ? Math.max(listBackup.length, (page + 2) * PAGE_SIZE)
    : listBackup.length;

  const handlePageChange = (nextPage) => {
    setPage(nextPage);
    loadBackupPages([nextPage + 2]);
  };

  const columns = [
    {
      headerName: "Pedido",
      field: "id",
      type: "string",
      headerAlign: "left",
      align: "left",
      width: 70,
    },
    {
      headerName: "Fecha",
      field: "date",
      type: "string",
      headerAlign: "left",
      align: "left",
      width: 80,
    },
    {
      headerName: "Hora",
      field: "time",
      type: "string",
      headerAlign: "left",
      align: "left",
      width: 80,
    },
    {
      headerName: "Tipo",
      field: "booking_type",
      type: "string",
      headerAlign: "left",
      align: "left",
      width: 110,
      renderCell: ({ row: { booking_type } }) => {
        return <>{BOOKING_TYPE_LABELS[booking_type] || booking_type}</>;
      },
    },
    {
      headerName: "Estado",
      field: "booking_status",
      type: "string",
      headerAlign: "left",
      align: "left",
      width: 120,
    },
    {
      headerName: "Nombre",
      field: "client_name",
      width: 200,
      renderCell: ({ row: { client_name, client_lastname } }) => {
        return (
          <>
            {client_name} {client_lastname}
          </>
        );
      },
    },
    {
      headerName: "EMPRESA",
      field: "enterprise_name",
      type: "string",
      width: 140,
    },
    {
      headerName: "Origen",
      field: "origin_name",
      type: "string",
      headerAlign: "left",
      align: "left",
      width: 210,
    },
    {
      headerName: "Destino",
      field: "destination_name",
      type: "string",
      headerAlign: "left",
      align: "left",
      width: 210,
      renderCell: ({ row: { destination_name, sub_routes } }) => {
        const subRoutesTooltip = getSubRoutesTooltip(sub_routes);

        if (!subRoutesTooltip) return <>{destination_name}</>;

        return (
          <Tooltip
            title={
              <Typography fontSize={12} sx={{ whiteSpace: "pre-line" }}>
                {subRoutesTooltip}
              </Typography>
            }
            arrow
          >
            <Box
              component="span"
              sx={{
                cursor: "help",
                overflow: "hidden",
                textOverflow: "ellipsis",
                whiteSpace: "nowrap",
              }}
            >
              {destination_name}
            </Box>
          </Tooltip>
        );
      },
    },
    {
      headerName: "Pack",
      field: "persons",
      headerAlign: "left",
      align: "center",
      width: 20,
    },
    {
      headerName: "Notas",
      field: "notes",
      width: 180,
    },
    {
      headerName: "Creado",
      field: "created_by",
      width: 80,
    },
  ];

  const createBookingFirebase = async (e) => {
    try {
      setIsLoading(true);
      e.preventDefault();
      dataSelected.map(async (book, idx) => {
        const data = await createDataFirebase(book);

        if (book.user_type == 0) {
          const counter = await exitsData(
            data.counterBooking,
            listBookingBusines,
          );
          if (counter > 0) {
            setIsLoading(false);
            return snackBarDialog("ESTE PEDIDO YA EXISTE", "error");
          } else {
            await restoreEnterprise(data);
            setIsLoading(false);
            return snackBarDialog("PEDIDO RESTAURADO");
          }
        } else {
          const counter = await exitsData(data.counterBooking, listBookingNow);
          if (counter > 0) {
            setIsLoading(false);
            return snackBarDialog("ESTE PEDIDO YA EXISTE", "error");
          } else {
            const response = await restoreClient(data);
            setIsLoading(false);
            return snackBarDialog("PEDIDO RESTAURADO");
          }
        }
      });
    } catch (error) {
      setIsLoading(false);
      snackBarDialog("HA OCURRIDO UN ERROR EN LA PAGINA", "error");
      console.log(error);
    }
  };

  const [listBookingNow, setListBookingNow] = useState([]);
  const [listBookingBusines, setListBookingBusiness] = useState([]);

  useEffect(() => {
    const getListBookingNow = async () => {
      const starCountRef = ref(db, "bookingNow");

      onValue(starCountRef, (snapshot) => {
        if (snapshot.exists()) {
          setListBookingNow([]);
          Object.values(snapshot.val()).map((item) => {
            setListBookingNow((oldData) => [...oldData, item]);
          });
        }
      });
    };
    const getListBookingBusiness = async () => {
      const starCountRef = ref(db, "BookingBusiness");
      onValue(starCountRef, (snapshot) => {
        if (snapshot.exists()) {
          setListBookingBusiness([]);
          Object.values(snapshot.val()).map((item) => {
            setListBookingBusiness((oldData) => [...oldData, item]);
          });
        }
      });
    };

    getListBookingNow();
    getListBookingBusiness();
  }, []);

  const exitsData = async (orderNumber, listBooking) => {
    let counter = 0;
    listBooking.map((book) => {
      if (orderNumber === book.counterBooking) {
        counter++;
      }
    });
    return counter;
  };

  const restoreClient = async (data) => {
    try {
      const dbRef = `bookingNow/${data.uuid}`;
      await set(ref(db, dbRef), data);
      snackBarDialog("PEDIDO RESTAURADO EN FIREBASE");
    } catch (error) {
      snackBarDialog("NO SE LOGRO RESTAURAR EN PEDIDOS CLIENTE", "error");
      console.log(error);
    }
  };

  const restoreEnterprise = async (data) => {
    try {
      const dbRef = `BookingBusiness/${data.uuid}`;
      await set(ref(db, dbRef), data);
      snackBarDialog("PEDIDO RESTAURADO EN FIREBASE");
    } catch (error) {
      snackBarDialog("ERROR: NO SE LOGRO RESTAURAR", "error");
      console.log(error);
    }
  };

  const createDataFirebase = async (values) => {
    const data = {
      uuid: values.uuid,
      client: values.user_id,
      clientName: `${values.client_name} ${values.client_lastname}`,
      clientEmail: values.client_email,
      clientPhone: values.client_phone,
      enterpriseId: values.enterprise_id ? values.enterprise_id : "",
      enterpriseName: values.enterprise_name ? values.enterprise_name : "",
      origin: {
        name: values.origin_name,
        lat: values.origin_lat,
        lng: values.origin_lng,
      },
      destination: {
        name: values.destination_name,
        lat: values.destination_lat,
        lng: values.destination_lng,
      },
      otherPersonName: values.other_name,
      otherPersonPhone: values.other_phone,
      subRoutes: getSubRoutesList(values.sub_routes),
      persons: values.persons,
      vehicle: values.vehicle_model,
      date: values.date,
      time: values.time,
      dateEnd: values.date_end,
      timeEnd: values.date_end,
      tour_id: values.tour_id > 0 ? values.tour_id : "",
      tour_description: values.description ? values.description : "",
      note: values.notes.toUpperCase(),
      noteAdmin: values.notes_admin,
      currency: values.currency,
      payment_type: values.payment_type,
      payment_voucher: values.payment_voucher,
      tax: values.tax,
      toll: values.toll,
      price: values.price,
      total: values.total,
      advance: values.advance,
      discount: values.discount,
      statusService: "PENDIENTE",
      statusPayment: "PENDIENTE",
      statusBooking: "PENDIENTE",
      createBooking: values.created_by,
      booking_type: values.booking_type,
      counterBooking: values.id,
      created: new Date(values.created_at).getTime(),
      isCreated: false,
      cotizar: Boolean(values.quote),
      cotizado: Boolean(values.quote),
      phoneActive: true,
      companyId: values.company_id ?? 1,
    };

    return data;
  };

  //SNACKBAR
  const [isLoading, setIsLoading] = useState(false);
  const [messageSnk, setMessageSnk] = useState("");
  const [stateSnk, setStateSnk] = useState(false);
  const [severity, setSeverity] = useState("success");
  const vertical = "top";
  const horizontal = "center";

  const handleSnkClose = () => setStateSnk(false);
  const snackBarDialog = (message, severity = "success") => {
    setMessageSnk(message);
    setSeverity(severity);
    setStateSnk(true);
  };

  return (
    <>
      <CssBaseline />
      <div className="app">
        {!matchesSM && <Sidebar />}
        <main className="content">
          <Topbar />
          <Box
            display="flex"
            justifyContent="space-between"
            alignItems="center"
            m={2}
          >
            <Header title="BACKUPS EMPRESA" subtitle="" />
            {dataSelected.length > 0 && (
              <Button
                variant="contained"
                color="success"
                onClick={createBookingFirebase}
                disabled={isLoading}
              >
                RESTAURAR A FIREBASE
              </Button>
            )}
          </Box>

          <Box
            m="10px 0 0 0"
            height="100vh"
            sx={{
              "& .MuiDataGrid-root": {
                border: "none",
              },
              "& .MuiDataGrid-cell": {
                borderBottom: "none",
              },
              "& .name-column--cell": {
                color: colors.greenAccent[300],
              },
              "& .MuiDataGrid-columnHeaders": {
                backgroundColor: colors.blueAccent[700],
                borderBottom: "none",
              },
              "& .MuiDataGrid-virtualScroller": {
                backgroundColor: colors.primary[400],
              },
              "& .MuiDataGrid-footerContainer": {
                borderTop: "none",
                backgroundColor: colors.blueAccent[700],
              },
              "& .MuiCheckbox-root": {
                color: `${colors.greenAccent[200]} !important`,
              },
            }}
          >
            <DataGrid
              getRowId={(row) => row.id}
              rows={rows}
              columns={columns}
              pagination
              paginationMode="server"
              page={page}
              pageSize={PAGE_SIZE}
              rowsPerPageOptions={[PAGE_SIZE]}
              rowCount={rowCount}
              onPageChange={handlePageChange}
              localeText={esES.components.MuiDataGrid.defaultProps.localeText}
              checkboxSelection
              onSelectionModelChange={(ids) => {
                setDataSelected([]);
                ids.forEach((id) => {
                  const select = listBackup.find((row) => row.id === id);
                  setDataSelected((oldData) => [...oldData, select]);
                });
              }}
            />
          </Box>

          <SnackBar
            stateSnk={stateSnk}
            vertical={vertical}
            horizontal={horizontal}
            duration={5000}
            handleClose={handleSnkClose}
            severity={severity}
            messageSnk={messageSnk}
          />
        </main>
      </div>
    </>
  );
};

export default Backup;
